import { useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tokenState } from '@/auth/tokenState';

interface AuthMeResponse {
  success: boolean;
  status: number;
  message: string;
  metadata: {
    timestamp: string;
    version: string;
  };
  data: {
    user: {
      username: string;
      email: string;
      first_name: string;
      last_name: string;
      primary_site: string;
      primary_site_role: string;
      sites: Array<{
        site_id: string;
        site_name: string;
        role: string;
        is_primary: boolean;
      }>;
    };
  };
}

export const useAuthValidation = () => {
  const validateAuth = useCallback(async () => {
    const accessToken = await tokenState.getAccessToken();
    
    if (!accessToken) {
      throw new Error('No access token');
    }

    const apiBaseUrl = `https://alto-cero-backend-multi-property-fwh3d3cybgdrdke5.southeastasia-01.azurewebsites.net/api`
    // const host = window.location.hostname;
    // const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 
    //   `${window.location.protocol}//${host}/api`;

    const response = await axios.get<AuthMeResponse>(`${apiBaseUrl}/auth/me/`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response.data;
  }, []);

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['auth-validation'],
    queryFn: validateAuth,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  useEffect(() => {
    if (error) {
      const errorResponse = (error as any)?.response;
      const errorDetail = errorResponse?.data?.detail;
      
      // Check for invalid token error
      if (
        errorResponse?.status === 401 || 
        errorDetail === "Invalid token." || 
        errorDetail === "Invalid token"
      ) {
        // Clear tokens and redirect to login
        tokenState.clearTokens();
        window.location.href = '/login';
      }
    }
  }, [error]);

  return {
    isAuthenticated: !!data?.success,
    user: data?.data?.user,
    isLoading,
    error,
    revalidate: refetch,
  };
};